import { useState, useEffect, useRef, forwardRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../App";
import api from "../services/api";
import "../styles/Checkout.css";
import { BASE_URL } from "../config";

// ── MUI ──────────────────────────────────────────────────────────
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Slide from "@mui/material/Slide";

// ── React-icons ──────────────────────────────────────────────────
import {
  RiMapPin2Line,
  RiShieldCheckLine,
  RiSmartphoneLine,
  RiMoneyDollarCircleLine,
  RiCheckLine,
  RiArrowRightLine,
  RiArrowLeftLine,
  RiEditLine,
  RiTruckLine,
  RiUserLine,
  RiMailLine,
  RiPhoneLine,
  RiMapPinLine,
  RiBankCardLine,
  RiInformationLine,
  RiLoader4Line,
  RiAlertLine,
  RiLockLine,
  RiShoppingBagLine,
  RiCheckboxCircleFill,
  RiCloseLine,
  RiArrowRightSLine,
} from "react-icons/ri";

// ── MUI slide-up transition ──────────────────────────────────────
const SlideUp = forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ── Indian states ────────────────────────────────────────────────
const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
];

// ── Razorpay loader ──────────────────────────────────────────────
const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error("Failed to load Razorpay"));
    document.body.appendChild(script);
  });

// ── Field wrapper ────────────────────────────────────────────────
function Field({ label, icon, error, children }) {
  return (
    <div className="nd-ckout-group">
      <label className="nd-ckout-label">
        {icon}
        {label}
      </label>
      {children}
      {error && (
        <p className="nd-ckout-error-msg">
          <RiAlertLine />
          {error}
        </p>
      )}
    </div>
  );
}

// ── Text input ───────────────────────────────────────────────────
function Input({ error, ...props }) {
  return (
    <input className={`nd-ckout-input${error ? " error" : ""}`} {...props} />
  );
}

// ── Order Success Modal (MUI) ─────────────────────────────────────
function OrderSuccessModal({ open, orderId, onClose }) {
  return (
    <Dialog
      open={open}
      TransitionComponent={SlideUp}
      keepMounted
      maxWidth="xs"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 6,
          overflow: "hidden",
          fontFamily: "'DM Sans', sans-serif",
          boxShadow: "0 32px 80px rgba(0,0,0,0.22)",
        },
      }}
    >
      <DialogContent style={{ padding: 0 }}>
        {/* Animated green header */}
        <div className="nd-ckout-modal-header">
          <div className="nd-ckout-modal-check-wrap">
            <RiCheckboxCircleFill className="nd-ckout-modal-check-icon" />
          </div>
          <div className="nd-ckout-modal-confetti" aria-hidden>
            {[...Array(14)].map((_, i) => (
              <span
                key={i}
                className="nd-ckout-confetti-dot"
                style={{ "--i": i }}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="nd-ckout-modal-body">
          <h2 className="nd-ckout-modal-title">Order Placed! 🎉</h2>
          <p className="nd-ckout-modal-sub">
            Thank you for shopping with NaturesDye. Your order is confirmed and
            being processed.
          </p>

          {orderId && (
            <div className="nd-ckout-modal-orderid">
              <span className="nd-ckout-modal-orderid-label">Order ID</span>
              <span className="nd-ckout-modal-orderid-val">{orderId}</span>
            </div>
          )}

          <div className="nd-ckout-modal-timeline">
            {[
              {
                icon: "📦",
                label: "Confirmed",
                sub: "Order received & verified",
              },
              {
                icon: "🏭",
                label: "Processing",
                sub: "Being packed for dispatch",
              },
              {
                icon: "🚚",
                label: "Ships in 1–2 days",
                sub: "Courier pickup scheduled",
              },
              {
                icon: "📬",
                label: "Delivered in 4–7 days",
                sub: "To your doorstep",
              },
            ].map((s, i) => (
              <div key={i} className="nd-ckout-modal-tl-row">
                <div className="nd-ckout-modal-tl-dot">
                  <span>{s.icon}</span>
                  {i < 3 && <div className="nd-ckout-modal-tl-line" />}
                </div>
                <div className="nd-ckout-modal-tl-text">
                  <strong>{s.label}</strong>
                  <span>{s.sub}</span>
                </div>
              </div>
            ))}
          </div>

          <button className="nd-ckout-modal-cta" onClick={onClose}>
            View My Orders <RiArrowRightSLine size={20} />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Error Modal (plain, no MUI needed) ──────────────────────────
function ErrorModal({ message, onClose }) {
  if (!message) return null;
  return (
    <div className="nd-ckout-error-overlay" onClick={onClose}>
      <div
        className="nd-ckout-error-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="nd-ckout-error-close" onClick={onClose}>
          <RiCloseLine size={20} />
        </button>
        <div className="nd-ckout-error-emoji">⚠️</div>
        <h3 className="nd-ckout-error-title">Something went wrong</h3>
        <p className="nd-ckout-error-body">{message}</p>
        <button
          className="nd-ckout-btn nd-ckout-btn-primary"
          style={{ width: "100%", marginTop: 12 }}
          onClick={onClose}
        >
          Got it
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════
export default function CheckoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, cartTotal, clearCart, user } = useApp();

  const { discountAmt = 0, couponCode = "" } = location.state || {};

  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [payment, setPayment] = useState("upi");
  const [step, setStep] = useState(1);
  const [placing, setPlacing] = useState(false);
  const [successModal, setSuccessModal] = useState({
    open: false,
    orderId: "",
  });
  const [errorMsg, setErrorMsg] = useState("");

  const [form, setForm] = useState({
    firstName: user?.name?.split(" ")[0] || "",
    lastName: user?.name?.split(" ").slice(1).join(" ") || "",
    email: user?.email || "",
    phone: user?.phone || "",
    address: "",
    city: "",
    state: "Tamil Nadu",
    pincode: "",
  });
  const [errors, setErrors] = useState({});

  // ── Stable refs so Razorpay callbacks never get stale closures ──
  const stableRefs = useRef({});
  stableRefs.current.clearCart = clearCart;
  stableRefs.current.navigate = navigate;
  stableRefs.current.setPlacing = setPlacing;
  stableRefs.current.setSuccessModal = setSuccessModal;
  stableRefs.current.setErrorMsg = setErrorMsg;

  useEffect(() => {
    loadRazorpayScript()
      .then(() => setRazorpayLoaded(true))
      .catch(() =>
        setErrorMsg("Payment system unavailable. Please use Cash on Delivery."),
      );
  }, []);

  // ── Totals ───────────────────────────────────────────────────
  const shipping = cartTotal >= 1000 ? 0 : 80;
  const codFee = payment === "cod" ? 40 : 0;
  const total = cartTotal - discountAmt + shipping + codFee;

  // ── Validation ───────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!form.firstName) errs.firstName = "Required";
    if (!form.lastName) errs.lastName = "Required";
    if (!form.email || !form.email.includes("@"))
      errs.email = "Valid email required";
    if (!form.phone || form.phone.length < 10)
      errs.phone = "Valid 10-digit phone required";
    if (!form.address) errs.address = "Required";
    if (!form.city) errs.city = "Required";
    if (!form.pincode || form.pincode.length < 6)
      errs.pincode = "Valid 6-digit pincode required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  // ── Build payload ─────────────────────────────────────────────
  const buildOrderPayload = (razorpayPaymentId = null) => ({
    items: cart.map((i) => ({
      productId: i.productId,
      name: i.name,
      brand: i.brand,
      image: i.image,
      price: i.price,
      qty: i.qty,
      size: i.size,
      color: i.color || null,
    })),
    shippingAddress: {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone,
      address: form.address,
      city: form.city,
      state: form.state,
      pincode: form.pincode,
    },
    paymentMethod: payment,
    couponCode,
    subtotal: cartTotal,
    shippingCost: shipping,
    codFee,
    discountAmount: discountAmt,
    totalAmount: total,
    ...(razorpayPaymentId && { razorpayPaymentId }),
  });

  // ── COD ───────────────────────────────────────────────────────
  const handleCOD = async () => {
    if (!validate()) return;
    setPlacing(true);
    try {
      const order = await api.orders.place(buildOrderPayload());
      await clearCart();
      setSuccessModal({ open: true, orderId: order.orderId });
    } catch (err) {
      setErrorMsg(
        err.response?.data?.message ||
          "Failed to place order. Please try again.",
      );
    } finally {
      setPlacing(false);
    }
  };

  // ── UPI / Razorpay ────────────────────────────────────────────
  const handleUPI = async () => {
    if (!validate()) return;
    if (!window.Razorpay) {
      try {
        await loadRazorpayScript();
      } catch {
        setErrorMsg(
          "Payment system unavailable. Please use COD or refresh the page.",
        );
        return;
      }
    }

    setPlacing(true);

    try {
      const response = await api.orders.createRazorpayOrder({
        totalAmount: total,
      });
      const { razorpayOrderId, amount, currency, keyId } = response;

      // Snapshot payload NOW — before any async callback fires
      const snapshotPayload = buildOrderPayload();

      const rzpOptions = {
        key: keyId,
        amount,
        currency,
        name: "NaturesDye",
        description: `Order – ${cart.length} item(s)`,
        order_id: razorpayOrderId,
        prefill: {
          name: `${form.firstName} ${form.lastName}`,
          email: form.email,
          contact: form.phone,
        },
        theme: { color: "#121212" },

        // ✅ Uses stableRefs — never stale, never a TypeError
        handler: async function (resp) {
          try {
            const verifyResponse = await api.orders.verifyPayment({
              razorpayOrderId: resp.razorpay_order_id,
              razorpayPaymentId: resp.razorpay_payment_id,
              razorpaySignature: resp.razorpay_signature,
            });
            const finalPayload = {
              ...snapshotPayload,
              razorpayPaymentId: verifyResponse.razorpayPaymentId,
            };
            const order = await api.orders.place(finalPayload);
            await stableRefs.current.clearCart();
            stableRefs.current.setSuccessModal({
              open: true,
              orderId: order.orderId,
            });
          } catch (err) {
            stableRefs.current.setErrorMsg(
              err.message ||
                "Payment verified but order could not be saved. Please contact support.",
            );
          } finally {
            stableRefs.current.setPlacing(false);
          }
        },

        modal: {
          ondismiss: function () {
            // User closed the payment sheet — just reset placing state, no toast needed
            stableRefs.current.setPlacing(false);
          },
        },
      };

      const rzp = new window.Razorpay(rzpOptions);

      rzp.on("payment.failed", function (resp) {
        stableRefs.current.setPlacing(false);
        stableRefs.current.setErrorMsg(
          resp.error?.description || "Payment failed. Please try again.",
        );
      });

      rzp.open();
    } catch (err) {
      setErrorMsg(
        err.message || "Could not initiate payment. Please try again.",
      );
      setPlacing(false);
    }
  };

  const handlePlaceOrder = () =>
    payment === "cod" ? handleCOD() : handleUPI();

  // Navigate to account after closing success modal
  const handleSuccessClose = () => {
    setSuccessModal({ open: false, orderId: "" });
    navigate("/account");
  };

  const STEPS = [
    { num: 1, label: "Address" },
    { num: 2, label: "Payment" },
    { num: 3, label: "Review" },
  ];

  // ── Empty cart ────────────────────────────────────────────────
  if (cart.length === 0)
    return (
      <div className="nd-ckout-empty">
        <div className="container">
          <div className="nd-ckout-empty-icon">🛒</div>
          <h2 className="nd-ckout-empty-title">Your cart is empty!</h2>
          <p className="nd-ckout-empty-text">
            Add some items before checking out.
          </p>
          <Link className="btn btn-primary" to="/collection">
            Shop Now
          </Link>
        </div>
      </div>
    );

  // ── Main render ───────────────────────────────────────────────
  return (
    <div className="nd-ckout-page">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="nd-ckout-breadcrumb">
          <Link to="/">Home</Link>
          <span className="nd-ckout-breadcrumb-sep">/</span>
          <Link to="/cart">Cart</Link>
          <span className="nd-ckout-breadcrumb-sep">/</span>
          <span className="nd-ckout-breadcrumb-current">Checkout</span>
        </nav>

        {/* Stepper */}
        <div className="nd-ckout-stepper">
          {STEPS.map((s, i) => (
            <div key={s.num} className="nd-ckout-step-item">
              <div
                className={`nd-ckout-step-bubble ${step === s.num ? "active" : step > s.num ? "done" : ""}`}
              >
                {step > s.num ? <RiCheckLine size={16} /> : s.num}
              </div>
              <span
                className={`nd-ckout-step-label ${step === s.num ? "active" : step > s.num ? "done" : ""}`}
              >
                {s.label}
              </span>
              {i < STEPS.length - 1 && (
                <div
                  className={`nd-ckout-step-line ${step > s.num ? "filled" : ""}`}
                />
              )}
            </div>
          ))}
        </div>

        <div className="nd-ckout-layout">
          {/* ── FORM PANEL ─────────────────────────────────── */}
          <div className="nd-ckout-panel" key={step}>
            {/* STEP 1 — Address */}
            {step === 1 && (
              <>
                <div className="nd-ckout-section-title">
                  <RiMapPin2Line size={22} />
                  Shipping Address
                </div>

                <div className="nd-ckout-row">
                  <Field
                    label="First Name"
                    icon={<RiUserLine />}
                    error={errors.firstName}
                  >
                    <Input
                      value={form.firstName}
                      onChange={(e) =>
                        handleChange("firstName", e.target.value)
                      }
                      placeholder="Rajesh"
                      error={errors.firstName}
                    />
                  </Field>
                  <Field
                    label="Last Name"
                    icon={<RiUserLine />}
                    error={errors.lastName}
                  >
                    <Input
                      value={form.lastName}
                      onChange={(e) => handleChange("lastName", e.target.value)}
                      placeholder="Kumar"
                      error={errors.lastName}
                    />
                  </Field>
                </div>

                <div className="nd-ckout-row">
                  <Field
                    label="Email Address"
                    icon={<RiMailLine />}
                    error={errors.email}
                  >
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="you@email.com"
                      error={errors.email}
                    />
                  </Field>
                  <Field
                    label="Phone Number"
                    icon={<RiPhoneLine />}
                    error={errors.phone}
                  >
                    <Input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      placeholder="9843961449"
                      maxLength={10}
                      error={errors.phone}
                    />
                  </Field>
                </div>

                <Field
                  label="Street Address"
                  icon={<RiMapPinLine />}
                  error={errors.address}
                >
                  <textarea
                    className={`nd-ckout-textarea${errors.address ? " error" : ""}`}
                    value={form.address}
                    onChange={(e) => handleChange("address", e.target.value)}
                    placeholder="House / Flat No., Street, Locality"
                  />
                </Field>

                <div className="nd-ckout-row">
                  <Field
                    label="City"
                    icon={<RiMapPin2Line />}
                    error={errors.city}
                  >
                    <Input
                      value={form.city}
                      onChange={(e) => handleChange("city", e.target.value)}
                      placeholder="Chennai"
                      error={errors.city}
                    />
                  </Field>
                  <Field
                    label="Pincode"
                    icon={<RiMapPinLine />}
                    error={errors.pincode}
                  >
                    <Input
                      type="number"
                      value={form.pincode}
                      onChange={(e) => handleChange("pincode", e.target.value)}
                      placeholder="600001"
                      maxLength={6}
                      error={errors.pincode}
                    />
                  </Field>
                </div>

                <Field label="State">
                  <select
                    className="nd-ckout-select"
                    value={form.state}
                    onChange={(e) => handleChange("state", e.target.value)}
                  >
                    {INDIAN_STATES.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Field>

                <button
                  className="nd-ckout-btn nd-ckout-btn-primary nd-ckout-btn-full"
                  onClick={() => {
                    if (validate()) setStep(2);
                  }}
                >
                  Continue to Payment <RiArrowRightLine size={16} />
                </button>
              </>
            )}

            {/* STEP 2 — Payment */}
            {step === 2 && (
              <>
                <div className="nd-ckout-section-title">
                  <RiBankCardLine size={22} />
                  Payment Method
                </div>

                <div className="nd-ckout-pay-options">
                  {[
                    {
                      id: "upi",
                      icon: <RiSmartphoneLine size={28} />,
                      label: "UPI Payment",
                      sub: "GPay · PhonePe · BHIM · Any UPI App",
                    },
                    {
                      id: "cod",
                      icon: <RiMoneyDollarCircleLine size={28} />,
                      label: "Cash on Delivery",
                      sub: "₹40 handling fee applies",
                    },
                  ].map((opt) => (
                    <div
                      key={opt.id}
                      className={`nd-ckout-pay-card ${payment === opt.id ? "selected" : ""}`}
                      onClick={() => setPayment(opt.id)}
                    >
                      <input
                        type="radio"
                        className="nd-ckout-pay-radio"
                        readOnly
                        checked={payment === opt.id}
                        id={`nd-pay-${opt.id}`}
                      />
                      <label
                        htmlFor={`nd-pay-${opt.id}`}
                        style={{ flex: 1, cursor: "pointer" }}
                      >
                        <div className="nd-ckout-pay-label">{opt.label}</div>
                        <div className="nd-ckout-pay-sublabel">{opt.sub}</div>
                      </label>
                      <span className="nd-ckout-pay-icon">{opt.icon}</span>
                    </div>
                  ))}
                </div>

                {payment === "upi" && (
                  <div className="nd-ckout-pay-info">
                    <RiInformationLine size={18} />
                    <div>
                      You'll be redirected to Razorpay's secure page to complete
                      payment.
                      {!razorpayLoaded && (
                        <div className="nd-ckout-rzp-loading">
                          <RiLoader4Line
                            style={{
                              animation: "nd-spin 0.65s linear infinite",
                            }}
                          />
                          Loading payment system…
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="nd-ckout-btn-row">
                  <button
                    className="nd-ckout-btn nd-ckout-btn-outline"
                    onClick={() => setStep(1)}
                  >
                    <RiArrowLeftLine size={15} />
                    Back
                  </button>
                  <button
                    className="nd-ckout-btn nd-ckout-btn-primary"
                    onClick={() => setStep(3)}
                  >
                    Review Order <RiArrowRightLine size={15} />
                  </button>
                </div>
              </>
            )}

            {/* STEP 3 — Review */}
            {step === 3 && (
              <>
                <div className="nd-ckout-section-title">
                  <RiShoppingBagLine size={22} />
                  Review Your Order
                </div>

                <div className="nd-ckout-review-box">
                  <div className="nd-ckout-review-tag">
                    <RiTruckLine />
                    Shipping To
                  </div>
                  <p className="nd-ckout-review-title">
                    {form.firstName} {form.lastName}
                  </p>
                  <p className="nd-ckout-review-text">
                    {form.address}, {form.city}, {form.state} – {form.pincode}
                    <br />
                    <RiPhoneLine style={{ verticalAlign: "middle" }} />{" "}
                    {form.phone}
                    &nbsp;&nbsp;|&nbsp;&nbsp;
                    <RiMailLine style={{ verticalAlign: "middle" }} />{" "}
                    {form.email}
                  </p>
                  <button
                    className="nd-ckout-review-edit"
                    onClick={() => setStep(1)}
                  >
                    <RiEditLine size={12} />
                    Edit
                  </button>
                </div>

                <div className="nd-ckout-review-box">
                  <div className="nd-ckout-review-tag">
                    <RiBankCardLine />
                    Payment Method
                  </div>
                  <p className="nd-ckout-review-title">
                    {
                      { upi: "UPI (via Razorpay)", cod: "Cash on Delivery" }[
                        payment
                      ]
                    }
                  </p>
                  <button
                    className="nd-ckout-review-edit"
                    onClick={() => setStep(2)}
                  >
                    <RiEditLine size={12} />
                    Edit
                  </button>
                </div>

                <p className="nd-ckout-terms">
                  By placing your order, you agree to our{" "}
                  <a href="#">Terms of Service</a> and{" "}
                  <a href="#">Privacy Policy</a>.
                </p>

                <div className="nd-ckout-btn-row">
                  <button
                    className="nd-ckout-btn nd-ckout-btn-outline"
                    onClick={() => setStep(2)}
                  >
                    <RiArrowLeftLine size={15} />
                    Back
                  </button>
                  <button
                    className="nd-ckout-btn nd-ckout-btn-primary"
                    style={{ fontSize: 14, paddingTop: 17, paddingBottom: 17 }}
                    onClick={handlePlaceOrder}
                    disabled={placing}
                  >
                    {placing ? (
                      <>
                        <span className="nd-ckout-spinner" />
                        {payment === "upi"
                          ? "Opening Payment…"
                          : "Placing Order…"}
                      </>
                    ) : payment === "upi" ? (
                      <>
                        <RiLockLine size={15} />
                        Pay ₹{total.toLocaleString()} via UPI
                      </>
                    ) : (
                      <>
                        <RiCheckLine size={15} />
                        Place Order — ₹{total.toLocaleString()}
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* ── ORDER SUMMARY ASIDE ──────────────────────── */}
          <aside className="nd-ckout-aside">
            <h3 className="nd-ckout-aside-title">Your Order</h3>

            {cart.map((item, idx) => (
              <div
                className="nd-ckout-item"
                key={item.key}
                style={{ animationDelay: `${idx * 0.06}s` }}
              >
                <div className="nd-ckout-item-img-wrap">
                  <img
                    src={`${BASE_URL}${item.image}`}
                    alt={item.name}
                    onError={(e) => {
                      e.target.src =
                        "https://placehold.co/90x110/f0e8dc/121212?text=IMG";
                    }}
                  />
                  <span className="nd-ckout-item-qty-badge">{item.qty}</span>
                </div>
                {/* Inside the cart mapping in CheckoutPage (around line 380-390) */}
                <div className="nd-ckout-item-info">
                  <div className="nd-ckout-item-name">{item.name}</div>
                  <div className="nd-ckout-item-variant">
                    Size: {item.size}
                    {item.color && (
                      <>
                        <span style={{ margin: "0 8px" }}>•</span>
                        Color: {item.color}
                      </>
                    )}
                  </div>
                </div>
                <div className="nd-ckout-item-price">
                  ₹{(item.price * item.qty).toLocaleString()}
                </div>
              </div>
            ))}

            <div className="nd-ckout-divider" />

            <div className="nd-ckout-summary-row">
              <span>Subtotal</span>
              <span>₹{cartTotal.toLocaleString()}</span>
            </div>
            {discountAmt > 0 && (
              <div className="nd-ckout-summary-row nd-ckout-summary-discount">
                <span>Discount</span>
                <span>−₹{discountAmt}</span>
              </div>
            )}
            <div className="nd-ckout-summary-row">
              <span>Shipping</span>
              <span>
                {shipping === 0 ? (
                  <span className="nd-ckout-summary-free">FREE</span>
                ) : (
                  `₹${shipping}`
                )}
              </span>
            </div>
            {payment === "cod" && (
              <div className="nd-ckout-summary-row">
                <span>COD Fee</span>
                <span>₹40</span>
              </div>
            )}

            <div className="nd-ckout-divider" />

            <div className="nd-ckout-summary-row total">
              <span>Total</span>
              <span>₹{total.toLocaleString()}</span>
            </div>

            <div className="nd-ckout-secure-badge">
              <RiShieldCheckLine size={20} />
              <span>
                Payments secured by Razorpay with 256-bit SSL encryption.
              </span>
            </div>
          </aside>
        </div>
      </div>

      {/* ── MUI Order Success Modal ─────────────────────────────── */}
      <OrderSuccessModal
        open={successModal.open}
        orderId={successModal.orderId}
        onClose={handleSuccessClose}
      />

      {/* ── Error Modal ──────────────────────────────────────────── */}
      <ErrorModal message={errorMsg} onClose={() => setErrorMsg("")} />
    </div>
  );
}
